import { OperationType } from '@aavaz-ai/eql-parser-ts/dist/types';
import { EMPTY_ARRAY } from '@modules/common/constants';
import { Category } from '@modules/common/types';
import { QuantifyFeedbackListDrawer } from '@modules/quantify/components/QuantifyFeedbackListDrawer';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import {
  useGetQuantifyCompareQuery,
  useGetQuantifyDuration,
  useGetQuantifyReportTableColumns,
  useGetQuantifyWhereClause,
} from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getCompareQueryContainer,
  getQuantifyQueryContainer,
} from '@modules/quantify/utils';
import { Box } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { QuantifyVisualisationType } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { TimeRange } from 'v2/core/entities';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';

interface QuantifyTableViewGridProps {
  shouldShowActions?: boolean;
  containerHeight: number;
  shouldShowMergeWithSelectedReasonsAction?: boolean;
}

export const QuantifyTableViewGrid = ({
  shouldShowActions = true,
  containerHeight,
  shouldShowMergeWithSelectedReasonsAction = true,
}: QuantifyTableViewGridProps) => {
  const muiGridRef = useGridApiRef();

  const query = useQuantifyStore(({ query }) => query);
  const compareQuery = useGetQuantifyCompareQuery();
  const tableRows = useQuantifyStore(
    ({ reportData }) =>
      (compareQuery
        ? reportData.compare.tableRows
        : reportData.quantify.tableRows) ?? EMPTY_ARRAY,
  );
  const activeVisualisationType = useQuantifyStore(
    ({ query }) => query.visualisationType,
  );
  const whereClause = useGetQuantifyWhereClause();
  const duration = useGetQuantifyDuration();

  const [selectedMetricKeyId, setSelectedMetricKeyId] = useState('');
  const [selectedMetricKeyLabel, setSelectedMetricKeyLabel] = useState('');
  const [selectedMetricKeyCategory, setSelectedMetricKeyCategory] =
    useState<Category>();
  const [isSelectedCompareFieldA, setIsSelectedCompareFieldA] = useState(false);
  const [isSelectedMetricKeyInvisible, setIsSelectedMetricKeyInvisible] =
    useState(false);
  const [
    isSelectedMetricKeyUpdateInProgress,
    setIsSelectedMetricKeyUpdateInProgress,
  ] = useState(false);

  const [feedbackQueryContainer, setFeedbackQueryContainer] =
    useState<QueryContainer | null>(null);

  const [shouldOpenFeedbackDrawer, toggleShouldOpenFeedbackDrawer] =
    useToggle(false);

  const showFeedbackDrawer = (params: ShowFeedbackDrawerFromTableOptions) => {
    toggleShouldOpenFeedbackDrawer(true);
    setSelectedMetricKeyId(params.id);
    setSelectedMetricKeyLabel(params.label);
    setSelectedMetricKeyCategory(params.category);
    setIsSelectedCompareFieldA(params.isCompareFieldA ?? false);
    setIsSelectedMetricKeyInvisible(params.isInvisible);
    setIsSelectedMetricKeyUpdateInProgress(params.isUpdateInProgress);

    const queryContainer = compareQuery
      ? getCompareQueryContainer({
          query,
          isFieldA: params.isCompareFieldA ?? false,
          metricKeyId: params.id,
          metricCategory: params.category,
          whereClause,
          compareQuery,
          duration,
        })
      : getQuantifyQueryContainer({
          query,
          metricKeyId: params.id,
          metricCategory: params.category,
          dateRange: params.dateRange ?? (duration as TimeRange),
          whereClause:
            params.whereClause && whereClause
              ? combineWhereClauses(
                  whereClause,
                  params.whereClause,
                  OperationType.AND,
                )
              : whereClause || params.whereClause,
        });

    setFeedbackQueryContainer(queryContainer);
  };

  const { columns } = useGetQuantifyReportTableColumns({
    onFeedbackCountClick: showFeedbackDrawer,
    shouldHideActions: !shouldShowActions,
    formulaID: query.formulaQuery?.eqlFunction,
    isCompactTableVariant: true,
    shouldShowAddToFilterForBothCompareFields: false,
    shouldShowCompareFieldHeaderTooltip: true,
    shouldShowMergeWithSelectedReasonsAction,
  });

  const shouldPinLabelColumn =
    activeVisualisationType === QuantifyVisualisationType.Trend;

  useEffect(() => {
    if (!muiGridRef?.current || !shouldPinLabelColumn) {
      return;
    }

    const feedbackCountColumnIndex = muiGridRef.current.getColumnIndex(
      'value',
      false,
    );

    if (feedbackCountColumnIndex === -1) {
      return;
    }

    // https://github.com/mui/mui-x/issues/6411
    const timer = setTimeout(() => {
      muiGridRef.current.scrollToIndexes({
        colIndex: feedbackCountColumnIndex,
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [muiGridRef, columns, shouldPinLabelColumn]);

  return (
    <>
      <Box
        sx={{
          px: 1,
          height: containerHeight ? `${containerHeight}px` : '350px',
          width: '100%',
        }}
      >
        <DataGridPro
          apiRef={muiGridRef}
          sx={{
            border: 'none',
            '& .MuiDataGrid-row:hover': {
              '.js-quantify-action-btn': {
                opacity: 1,
              },
            },
          }}
          rows={tableRows}
          columns={columns}
          getRowId={({ id }) => id}
          pinnedColumns={
            shouldPinLabelColumn
              ? {
                  left: ['label'],
                }
              : undefined
          }
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 40}
          columnHeaderHeight={45}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
        />
      </Box>
      {feedbackQueryContainer && (
        <QuantifyFeedbackListDrawer
          open={shouldOpenFeedbackDrawer}
          onClose={toggleShouldOpenFeedbackDrawer}
          queryContainer={feedbackQueryContainer}
          metricKeyLabel={selectedMetricKeyLabel}
          metricKeyId={selectedMetricKeyId}
          metricKeyCategory={selectedMetricKeyCategory}
          isCompareFieldA={isSelectedCompareFieldA}
          isMetricKeyInvisible={isSelectedMetricKeyInvisible}
          isMetricKeyUpdateInProgress={isSelectedMetricKeyUpdateInProgress}
        />
      )}
    </>
  );
};
